import React from 'react';
import { isNodeSelection } from '@tiptap/react';
import { Editor } from '@tiptap/react';
import { useRichTextTiptapEditor } from 'apps/sigma2-ui/src/components/rich-text/tiptap/hooks/use-tiptap-editor';
import { RichTextBoldIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/bold-icon';
import { RichTextCode2Icon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/code2-icon';
import { RichTextItalicIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/italic-icon';
import { RichTextStrikeIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/strike-icon';
import { RichTextSubscriptIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/subscript-icon';
import { RichTextSuperscriptIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/superscript-icon';
import { RichTextUnderlineIcon } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-icons/underline-icon';
import { richTextIsMarkInSchema } from 'apps/sigma2-ui/src/components/rich-text/tiptap/lib/tiptap-utils';
import { RichTextButtonProps } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';
import { RichTextButton } from 'apps/sigma2-ui/src/components/rich-text/tiptap/components/tiptap-ui-primitive/button/button';

export type RichTextMark =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'code'
  | 'underline'
  | 'superscript'
  | 'subscript';

export interface RichTextMarkButtonProps
  extends Omit<RichTextButtonProps, 'type'> {
  /**
   * The type of mark to toggle
   */
  type: RichTextMark;
  /**
   * Optional editor instance. If not provided, will use editor from context
   */
  editor?: Editor | null;
  /**
   * Display text for the button (optional)
   */
  text?: string;
  /**
   * Whether this button should be hidden when the mark is not available
   */
  hideWhenUnavailable?: boolean;
}

export const richTextMarkIcons = {
  bold: RichTextBoldIcon,
  italic: RichTextItalicIcon,
  underline: RichTextUnderlineIcon,
  strike: RichTextStrikeIcon,
  code: RichTextCode2Icon,
  superscript: RichTextSuperscriptIcon,
  subscript: RichTextSubscriptIcon,
};

export const richTextMarkShortcutKeys: Partial<Record<RichTextMark, string>> = {
  bold: 'Ctrl-b',
  italic: 'Ctrl-i',
  underline: 'Ctrl-u',
  strike: 'Ctrl-Shift-s',
  code: 'Ctrl-e',
  superscript: 'Ctrl-.',
  subscript: 'Ctrl-,',
};

export function richTextCanToggleMark(
  editor: Editor | null,
  type: RichTextMark,
): boolean {
  if (!editor) return false;

  try {
    return editor.can().toggleMark(type);
  } catch {
    return false;
  }
}

export function richTextIsMarkActive(
  editor: Editor | null,
  type: RichTextMark,
): boolean {
  if (!editor) return false;
  return editor.isActive(type);
}

export function richTextToggleMark(
  editor: Editor | null,
  type: RichTextMark,
): void {
  if (!editor) return;
  editor.chain().focus().toggleMark(type).run();
}

export function richTextIsMarkButtonDisabled(
  editor: Editor | null,
  type: RichTextMark,
  userDisabled: boolean = false,
): boolean {
  if (!editor) return true;
  if (userDisabled) return true;
  if (editor.isActive('codeBlock')) return true;
  if (!richTextCanToggleMark(editor, type)) return true;
  return false;
}

export function richTextShouldShowMarkButton(params: {
  editor: Editor | null;
  type: RichTextMark;
  hideWhenUnavailable: boolean;
  markInSchema: boolean;
}): boolean {
  const { editor, type, hideWhenUnavailable, markInSchema } = params;

  if (!markInSchema || !editor) {
    return false;
  }

  if (hideWhenUnavailable) {
    if (
      isNodeSelection(editor.state.selection) ||
      !richTextCanToggleMark(editor, type)
    ) {
      return false;
    }
  }

  return true;
}

export function richTextGetFormattedMarkName(type: RichTextMark): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function useRichTextMarkState(
  editor: Editor | null,
  type: RichTextMark,
  disabled: boolean = false,
) {
  const markInSchema = richTextIsMarkInSchema(type, editor);
  const isDisabled = richTextIsMarkButtonDisabled(editor, type, disabled);
  const isActive = richTextIsMarkActive(editor, type);

  const Icon = richTextMarkIcons[type];
  const shortcutKey = richTextMarkShortcutKeys[type];
  const formattedName = richTextGetFormattedMarkName(type);

  return {
    markInSchema,
    isDisabled,
    isActive,
    Icon,
    shortcutKey,
    formattedName,
  };
}

export const RichTextMarkButton = React.forwardRef<
  HTMLButtonElement,
  RichTextMarkButtonProps
>(
  (
    {
      editor: providedEditor,
      type,
      text,
      hideWhenUnavailable = false,
      className = '',
      disabled,
      onClick,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const editor = useRichTextTiptapEditor(providedEditor);

    const {
      markInSchema,
      isDisabled,
      isActive,
      Icon,
      shortcutKey,
      formattedName,
    } = useRichTextMarkState(editor, type, disabled);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(e);

        if (!e.defaultPrevented && !isDisabled && editor) {
          richTextToggleMark(editor, type);
        }
      },
      [onClick, isDisabled, editor, type],
    );

    const show = React.useMemo(() => {
      return richTextShouldShowMarkButton({
        editor,
        type,
        hideWhenUnavailable,
        markInSchema,
      });
    }, [editor, type, hideWhenUnavailable, markInSchema]);

    if (!show || !editor || !editor.isEditable) {
      return null;
    }

    return (
      <RichTextButton
        type="button"
        className={className.trim()}
        disabled={isDisabled}
        data-style="ghost"
        data-active-state={isActive ? 'on' : 'off'}
        data-disabled={isDisabled}
        role="button"
        tabIndex={-1}
        aria-label={type}
        aria-pressed={isActive}
        tooltip={formattedName}
        shortcutKeys={shortcutKey}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children || (
          <>
            <Icon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
          </>
        )}
      </RichTextButton>
    );
  },
);

RichTextMarkButton.displayName = 'MarkButton';

export default RichTextMarkButton;
